/**
 * MSAL Service - Centralized MSAL Instance Management
 *
 * Provides singleton pattern for MSAL initialization and management.
 * Eliminates hook-based singleton complexity and provides clean service interface.
 *
 * @module MsalService
 */

import { MsalError } from '@/common/utils/AuthErrors'

/**
 * Centralized MSAL service for authentication management.
 *
 * Features:
 * - True singleton pattern with proper initialization control
 * - Promise-based initialization to handle concurrent requests
 * - Clean error handling with typed errors
 * - Lazy loading of MSAL configuration to avoid circular dependencies
 *
 * @class MsalService
 */
class MsalService {
    constructor() {
        this.isInitialized = false
        this.initializationPromise = null
        this.instance = null
    }

    /**
     * Initializes MSAL instance with singleton pattern.
     * Multiple concurrent calls will share the same initialization promise.
     *
     * @returns {Promise<Object>} MSAL instance after successful initialization
     * @throws {MsalError} If initialization fails
     */
    async initialize() {
        // If already initialized, return immediately
        if (this.isInitialized && this.instance) {
            return this.instance
        }

        // If initialization is in progress, wait for it
        if (this.initializationPromise) {
            return this.initializationPromise
        }

        // Start new initialization
        this.initializationPromise = this._performInitialization()
        return this.initializationPromise
    }

    /**
     * Private method to perform the actual MSAL initialization.
     * Handles dynamic imports to avoid circular dependencies.
     *
     * @private
     * @returns {Promise<Object>} MSAL instance
     * @throws {MsalError} If initialization fails
     */
    async _performInitialization() {
        try {
            console.debug('[MSAL Service] Starting MSAL initialization...')

            // Dynamic import to avoid circular dependencies
            const { initializeMsal, msalInstance } = await import('@/authConfig')

            // Initialize MSAL using existing configuration
            await initializeMsal()

            this.instance = msalInstance
            this.isInitialized = true

            console.debug('[MSAL Service] MSAL initialization completed successfully')
            return this.instance
        } catch (error) {
            console.error('[MSAL Service] MSAL initialization failed:', error)

            // Reset state on failure to allow retry
            this.initializationPromise = null
            this.isInitialized = false
            this.instance = null

            throw new MsalError('Failed to initialize MSAL instance', error)
        }
    }

    /**
     * Gets the currently active user account from MSAL.
     * Automatically ensures MSAL is initialized before accessing account.
     *
     * @returns {Promise<Object|null>} Active account or null if not authenticated
     * @throws {MsalError} If MSAL is not available or initialization fails
     */
    async getActiveAccount() {
        try {
            const instance = await this.initialize()
            const account = instance.getActiveAccount()

            console.debug('[MSAL Service] Retrieved active account:', account ? 'Account found' : 'No active account')
            return account
        } catch (error) {
            console.error('[MSAL Service] Failed to get active account:', error)
            throw new MsalError('Failed to retrieve active account', error)
        }
    }

    /**
     * Gets the MSAL instance synchronously.
     * Throws error if MSAL is not initialized.
     *
     * @returns {Object} MSAL instance
     * @throws {Error} If MSAL not initialized
     */
    getInstance() {
        if (!this.isInitialized || !this.instance) {
            throw new Error('MSAL not initialized. Call initialize() first.')
        }
        return this.instance
    }

    /**
     * Gets all accounts from MSAL.
     * Automatically ensures MSAL is initialized.
     *
     * @returns {Promise<Array>} Array of all accounts
     * @throws {MsalError} If initialization fails
     */
    async getAllAccounts() {
        try {
            const instance = await this.initialize()
            return instance.getAllAccounts()
        } catch (error) {
            console.error('[MSAL Service] Failed to get all accounts:', error)
            throw new MsalError('Failed to retrieve accounts', error)
        }
    }

    /**
     * Sets the active account in MSAL.
     * Automatically ensures MSAL is initialized.
     *
     * @param {Object} account - Account to set as active
     * @returns {Promise<void>}
     * @throws {MsalError} If initialization fails
     */
    async setActiveAccount(account) {
        try {
            const instance = await this.initialize()
            instance.setActiveAccount(account)
            console.debug('[MSAL Service] Set active account:', account ? account.username : 'null')
        } catch (error) {
            console.error('[MSAL Service] Failed to set active account:', error)
            throw new MsalError('Failed to set active account', error)
        }
    }

    /**
     * Resets the service state (useful for testing or error recovery).
     * This should rarely be used in production code.
     *
     * @internal
     */
    reset() {
        console.warn('[MSAL Service] Resetting MSAL service state')
        this.isInitialized = false
        this.initializationPromise = null
        this.instance = null
    }

    /**
     * Gets the current initialization status.
     *
     * @returns {boolean} Whether MSAL is currently initialized
     */
    get initialized() {
        return this.isInitialized
    }

    /**
     * Gets whether initialization is currently in progress.
     *
     * @returns {boolean} Whether initialization is in progress
     */
    get initializing() {
        return !!this.initializationPromise && !this.isInitialized
    }
}

// Export singleton instance
export const msalService = new MsalService()
